import React, { useState, useEffect, useContext } from "react";
import { Dropdown } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaSearch, FaShoppingCart, FaUserCircle,
  FaChevronDown, FaChevronRight, FaBars, FaTimes, FaHeart,
} from "react-icons/fa";
import "../css/navbar.css";
import api from "../services/api";
import { getBranding } from "../services/branding";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "react-i18next";
import SearchBox from "../components/SearchBox";
import LanguageSwitcher from "../components/LanguageSwitcher";

const Navbar = () => {
  const [brand,              setBrand]              = useState(null);
  const [categoryMap,        setCategoryMap]        = useState({});
  const [expandedCat,        setExpandedCat]        = useState(null); // desktop hover
  const [showCatDropdown,    setShowCatDropdown]    = useState(false);
  const [showSearch,         setShowSearch]         = useState(false);
  const [mobileOpen,         setMobileOpen]         = useState(false);
  const [mobileExpandedCat,  setMobileExpandedCat]  = useState(null);

  const { isAuthenticated, logout } = useContext(AuthContext);
  const { cartItems } = useCart();
  const { enabled: wishlistEnabled, count: wishCount } = useWishlist();
  const { t } = useTranslation();
  const { enabled: langEnabled, languages, supported, current: currentLang, change: changeLang } = useLanguage();
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => { getBranding().then(setBrand); }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setShowSearch(false);
  }, [location.pathname]);

  // Fetch subcategories
  useEffect(() => {
    api.get("/subcategories")
      .then(res => {
        const grouped = {};
        res.data.forEach(sub => {
          const catName = sub.category_id?.name || "Other";
          if (!grouped[catName]) grouped[catName] = [];
          grouped[catName].push(sub);
        });
        setCategoryMap(grouped);
      })
      .catch(err => console.error("Error fetching subcategories:", err));
  }, []);

  // Close mobile on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 900) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ESC closes search
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") setShowSearch(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setMobileOpen(false);
  };

  const handleSubcategoryClick = () => {
    setShowCatDropdown(false);
    setMobileOpen(false);
    setMobileExpandedCat(null);
  };

  const cartCount = cartItems.length;

  return (
    <>
      {/* ─── Delivery bar ─── */}
      <div className="delivery-bar">
        {t("nav.deliveryBar")}
      </div>

      {/* ─── Navbar ─── */}
      <nav className="fmart-navbar">
        <div className="navbar-inner">

          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img src={brand?.logo_url || "/images/Logo1.png"} alt={brand?.store_name || "FMart"} />
          </Link>

          {/* ─── Desktop nav links ─── */}
          <div className="navbar-links">
            <Link to="/" className="nav-link-item">{t("nav.home")}</Link>
            <Link to="/about" className="nav-link-item">{t("nav.aboutUs")}</Link>

            {/* Desktop Categories dropdown */}
            <Dropdown show={showCatDropdown} onToggle={setShowCatDropdown}>
              <button
                className={`navbar-cat-btn ${showCatDropdown ? "active" : ""}`}
                onClick={() => setShowCatDropdown(v => !v)}
              >
                {t("nav.categories")}
                <FaChevronDown className={`cat-chevron ${showCatDropdown ? "rotated" : ""}`} />
              </button>

              <Dropdown.Menu className="cat-dropdown-menu">
                <div className="cat-menu-grid">
                  {Object.entries(categoryMap).map(([catName, subs]) => (
                    <div
                      className="cat-menu-group"
                      key={catName}
                      onMouseEnter={() => setExpandedCat(catName)}
                      onMouseLeave={() => setExpandedCat(null)}
                    >
                      {/* Category header — links to category page */}
                      <Link
                        to={`/categories/${subs[0]?.category_id?._id}/products`}
                        className="cat-group-header"
                        onClick={handleSubcategoryClick}
                      >
                        <span>{catName}</span>
                        <FaChevronRight className="cat-group-arrow" />
                      </Link>

                      {/* Subcategories — appear on hover */}
                      <div className={`cat-group-subs ${expandedCat === catName ? "visible" : ""}`}>
                        {subs.map(sub => (
                          <Link
                            key={sub._id}
                            to={`/subcategory/${sub._id}`}
                            className="cat-sub-link"
                            onClick={handleSubcategoryClick}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>

            <Link to="/shop" className="nav-link-item">{t("nav.shop")}</Link>
            <Link to="/contact" className="nav-link-item">{t("nav.contact")}</Link>
          </div>

          {/* ─── Right icons ─── */}
          <div className="navbar-icons">
            <button className="nav-icon-btn" onClick={() => setShowSearch(true)} aria-label="Search">
              <FaSearch />
            </button>

            {isAuthenticated ? (
              <Dropdown align="end" className="user-dd">
                <Dropdown.Toggle aria-label="Account">
                  <FaUserCircle />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item as={Link} to="/userprofile">{t("nav.myProfile")}</Dropdown.Item>
                  <Dropdown.Item as={Link} to="/my-orders">{t("nav.myOrders")}</Dropdown.Item>
                  <Dropdown.Item as={Link} to="/my-addresses">{t("nav.myAddresses")}</Dropdown.Item>
                  <Dropdown.Item as={Link} to="/change-password">{t("nav.changePassword")}</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item className="logout" onClick={handleLogout}>{t("nav.logout")}</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Link to="/login" className="nav-icon-btn" aria-label="Login">
                <FaUserCircle />
              </Link>
            )}

            {wishlistEnabled && (
              <Link to="/wishlist" className="nav-icon-btn" aria-label="Wishlist">
                <FaHeart />
                {wishCount > 0 && (
                  <span className="nav-cart-badge">{wishCount > 9 ? "9+" : wishCount}</span>
                )}
              </Link>
            )}

            <Link to="/cart" className="nav-icon-btn" aria-label="Cart">
              <FaShoppingCart />
              {cartCount > 0 && (
                <span className="nav-cart-badge">{cartCount > 9 ? "9+" : cartCount}</span>
              )}
            </Link>

            <LanguageSwitcher />

            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Mobile overlay backdrop ─── */}
      {mobileOpen && (
        <div className="mobile-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      {/* ─── Mobile drawer — slides in from right ─── */}
      <div className={`mobile-drawer ${mobileOpen ? "open" : ""}`}>
        {/* Drawer header */}
        <div className="mobile-drawer-header">
          <img src={brand?.logo_url || "/images/Logo1.png"} alt={brand?.store_name || "FMart"} className="mobile-drawer-logo" />
          <button className="mobile-drawer-close" onClick={() => setMobileOpen(false)}>
            <FaTimes />
          </button>
        </div>

        <div className="mobile-drawer-body">
          {/* Main nav links */}
          <div className="mobile-nav-section">
            <Link className="mobile-nav-link" to="/">{t("nav.home")}</Link>
            <Link className="mobile-nav-link" to="/about">{t("nav.aboutUs")}</Link>
            <Link className="mobile-nav-link" to="/shop">{t("nav.shop")}</Link>
            <Link className="mobile-nav-link" to="/contact">{t("nav.contact")}</Link>
          </div>

          {/* Language picker — only when the plan enables multi-language */}
          {langEnabled && languages.length > 1 && (
            <>
              <div className="mobile-section-divider">
                <span>{t("nav.language")}</span>
              </div>
              <div className="mobile-lang-row">
                {languages.map(code => (
                  <button
                    key={code}
                    className={`mobile-lang-btn ${currentLang === code ? "active" : ""}`}
                    onClick={() => changeLang(code)}
                  >
                    {supported.find(l => l.code === code)?.label || code.toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="mobile-section-divider">
            <span>{t("nav.categories")}</span>
          </div>

          {/* Categories — accordion style, tap to expand */}
          <div className="mobile-cats-section">
            {Object.entries(categoryMap).map(([catName, subs]) => (
              <div key={catName} className="mobile-cat-group">
                <button
                  className={`mobile-cat-header ${mobileExpandedCat === catName ? "expanded" : ""}`}
                  onClick={() => setMobileExpandedCat(
                    mobileExpandedCat === catName ? null : catName
                  )}
                >
                  <span>{catName}</span>
                  <FaChevronDown
                    className={`mobile-cat-chevron ${mobileExpandedCat === catName ? "rotated" : ""}`}
                  />
                </button>

                {/* Subcategory list — smooth accordion */}
                <div className={`mobile-cat-subs ${mobileExpandedCat === catName ? "open" : ""}`}>
                  {/* Link to entire category */}
                  <Link
                    to={`/categories/${subs[0]?.category_id?._id}/products`}
                    className="mobile-cat-sub all-link"
                    onClick={handleSubcategoryClick}
                  >
                    {t("nav.allCategory", { name: catName })}
                  </Link>
                  {subs.map(sub => (
                    <Link
                      key={sub._id}
                      to={`/subcategory/${sub._id}`}
                      className="mobile-cat-sub"
                      onClick={handleSubcategoryClick}
                    >
                      {sub.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mobile-section-divider">
            <span>{t("nav.account")}</span>
          </div>

          {/* Auth links */}
          <div className="mobile-nav-section">
            <Link className="mobile-nav-link cart-link" to="/cart">
              <span>{t("nav.cart")}</span>
              {cartCount > 0 && (
                <span className="mobile-cart-badge">{cartCount}</span>
              )}
            </Link>

            {isAuthenticated ? (
              <>
                <Link className="mobile-nav-link" to="/userprofile">{t("nav.myProfile")}</Link>
                <Link className="mobile-nav-link" to="/my-orders">{t("nav.myOrders")}</Link>
                <Link className="mobile-nav-link" to="/my-addresses">{t("nav.myAddresses")}</Link>
                <button className="mobile-nav-link mobile-logout-btn" onClick={handleLogout}>
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link className="mobile-nav-link mobile-login-btn" to="/login">{t("nav.login")}</Link>
                <Link className="mobile-nav-link mobile-register-btn" to="/register">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Search overlay ─── */}
      {showSearch && (
        <div
          className="search-overlay"
          onClick={e => { if (e.target === e.currentTarget) setShowSearch(false); }}
        >
          <div className="search-overlay-box">
            <button className="search-overlay-close" onClick={() => setShowSearch(false)}>
              <FaTimes />
            </button>
            <SearchBox onSelect={() => setShowSearch(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;